import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SingleValueData } from 'src/app/models/dashboard.model';

@Component({
  selector: 'app-ranking-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ranking-chart.component.html',
  styleUrls: ['./ranking-chart.component.scss']
})
export class RankingChartComponent implements OnInit, OnChanges {
  @Input() data: SingleValueData[] = [];
  @Input() title: string = '';
  @Input() animated: boolean = true;
  @Input() maxItems: number = 10;
  @Input() showPercentage: boolean = true;
  @Input() colorScheme: 'purple' | 'gradient' | 'rainbow' = 'gradient';

  processedData: {
    name: string;
    value: number;
    percentage: number;
    width: number;
    color: string;
    bgColor: string;
    rank: number;
  }[] = [];

  total: number = 0;
  maxValue: number = 0;

  // Cores gradiente bonitas
  private readonly gradientColors = [
    { bar: '#A0328B', bg: 'rgba(160, 50, 139, 0.15)' },  // Purple (primary)
    { bar: '#C23F9A', bg: 'rgba(194, 63, 154, 0.15)' },  // Pink
    { bar: '#E34DA8', bg: 'rgba(227, 77, 168, 0.15)' },  // Light Pink
    { bar: '#7C3AED', bg: 'rgba(124, 58, 237, 0.15)' },  // Violet
    { bar: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.15)' },  // Purple lighter
    { bar: '#A78BFA', bg: 'rgba(167, 139, 250, 0.15)' }, // Lavender
    { bar: '#C084FC', bg: 'rgba(192, 132, 252, 0.15)' }, // Mauve
    { bar: '#D946EF', bg: 'rgba(217, 70, 239, 0.15)' },  // Fuchsia
    { bar: '#EC4899', bg: 'rgba(236, 72, 153, 0.15)' },  // Pink
    { bar: '#F472B6', bg: 'rgba(244, 114, 182, 0.15)' }, // Light pink
  ];

  private readonly rainbowColors = [
    { bar: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)' },   // Red
    { bar: '#F97316', bg: 'rgba(249, 115, 22, 0.15)' },  // Orange
    { bar: '#EAB308', bg: 'rgba(234, 179, 8, 0.15)' },   // Yellow
    { bar: '#22C55E', bg: 'rgba(34, 197, 94, 0.15)' },   // Green
    { bar: '#14B8A6', bg: 'rgba(20, 184, 166, 0.15)' },  // Teal
    { bar: '#3B82F6', bg: 'rgba(59, 130, 246, 0.15)' },  // Blue
    { bar: '#6366F1', bg: 'rgba(99, 102, 241, 0.15)' },  // Indigo
    { bar: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.15)' },  // Purple
    { bar: '#EC4899', bg: 'rgba(236, 72, 153, 0.15)' },  // Pink
    { bar: '#F43F5E', bg: 'rgba(244, 63, 94, 0.15)' },   // Rose
  ];

  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.processData();
    }
  }

  processData(): void {
    if (!this.data || this.data.length === 0) {
      this.processedData = [];
      this.total = 0;
      return;
    }

    // Ordena por valor decrescente e pega os top N
    const sortedData = [...this.data]
      .sort((a, b) => (b.value || 0) - (a.value || 0))
      .slice(0, this.maxItems);

    // Calcula total e máximo
    this.total = this.data.reduce((sum, item) => sum + (item.value || 0), 0);
    this.maxValue = sortedData.length > 0 ? sortedData[0].value : 0;

    // Processa cada item
    this.processedData = sortedData.map((item, index) => {
      const percentage = this.total > 0 ? (item.value / this.total) * 100 : 0;
      const width = this.maxValue > 0 ? (item.value / this.maxValue) * 100 : 0;
      const colors = this.getColor(index);

      return {
        name: item.name || 'Não informado',
        value: item.value || 0,
        percentage,
        width: Math.max(width, 5), // Mínimo de 5% para visibilidade
        color: colors.bar,
        bgColor: colors.bg,
        rank: index + 1
      };
    });
  }

  private getColor(index: number): { bar: string; bg: string } {
    const colors = this.colorScheme === 'rainbow' ? this.rainbowColors : this.gradientColors;
    
    if (this.colorScheme === 'purple') {
      // Degradê de roxo
      const opacity = 1 - (index * 0.08);
      return {
        bar: `rgba(160, 50, 139, ${Math.max(opacity, 0.4)})`,
        bg: `rgba(160, 50, 139, ${Math.max(opacity * 0.15, 0.05)})`
      };
    }

    return colors[index % colors.length];
  }

  formatValue(value: number): string {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    }
    if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    }
    return value.toLocaleString('pt-BR');
  }

  formatPercentage(value: number): string {
    return value.toFixed(1) + '%';
  }

  getRankIcon(rank: number): string {
    switch (rank) {
      case 1: return '1';
      case 2: return '2';
      case 3: return '3';
      case 4: return '4';
      case 5: return '5';
      case 6: return '6';
      case 7: return '7';
      case 8: return '8';
      case 9: return '9';
      case 10: return '10';
      default: return '';
    }
  }
}
